/* eslint-disable prettier/prettier */
import React, { useState, useContext, useCallback, Suspense } from "react";
import {
	AppBar,
	CssBaseline,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemAvatar,
	Toolbar,
	Avatar,
	Badge,
	Menu,
	MenuItem,
	Accordion,
	AccordionSummary,
	makeStyles,
	useTheme,
	withStyles,
	Popover,
	Tooltip
} from "@material-ui/core";
import { Outlet, useNavigate } from "react-router-dom";
import { DialogProps } from "../../../@types/dialogProps.types";

import { UIContext, AuthContext } from "../../../contexts";
import SIDEBAR_OPTIONS from "../../../constants/sidebarOptions";
import { Tab } from "../../../@types/sidebarOptions.types";
import { curaFootLogoWhite } from "../../../assets";

import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MoreIcon from "@material-ui/icons/MoreVert";
import PersonIcon from "@material-ui/icons/Person";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import initCapitalize from "../../../utils/commonFunctions/initCapitalize";
import ROLES from "../../../constants/roles";

import "../superAdmin/sidebar/superAdminSidebar.css";
import { LazyCustomDialog } from "../../shared";

// eslint-disable-next-line arrow-parens
const useStyles = makeStyles((theme) => ({
	application: {
		display: "flex"
	},
	drawer: {},
	appBar: {},
	menuButton: {
		marginRight: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			backgroundColor: "#EEEEEE",
			color: "black"
		}
	},
	toolbar: theme.mixins.toolbar,
	drawerPaper: {},
	content: {
		flexGrow: 1,
		minHeight: "100vh",
		padding: theme.spacing(3)
	},
	avatarColor: {
		color: "black",
		padding: 40
	},
	sectionDesktop: {
		display: "none",
		[theme.breakpoints.up("md")]: {
			display: "flex"
		}
	},
	sectionMobile: {
		display: "flex",
		[theme.breakpoints.up("md")]: {
			display: "none"
		}
	},
	grow: {
		flexGrow: 1
	}
}));

// eslint-disable-next-line arrow-parens
const StyledBadge = withStyles((theme) => ({
	badge: {
		backgroundColor: "#25a18e",
		color: "#25a18e",
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		"&::after": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			borderRadius: "50%",
			animation: "$ripple 1.2s infinite ease-in-out",
			border: "1px solid currentColor",
			// eslint-disable-next-line quotes
			content: '""'
		}
	},
	"@keyframes ripple": {
		"0%": {
			transform: "scale(.8)",
			opacity: 1
		},
		"100%": {
			transform: "scale(2.4)",
			opacity: 0
		}
	}
}))(Badge);

const LabSidebar = (): JSX.Element => {
	const classes = useStyles();
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const [drawerWidth, setDrawerWidth] = useState(240);
	const [mobileOpen, setMobileOpen] = useState<boolean>(true);
	const [anchorEl, setAnchorEl] = useState<(EventTarget & (HTMLLIElement | HTMLButtonElement)) | null>(null);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const { dashboardHeader, selectedTab } = useContext(UIContext);

	const [logoutDialogProps, setLogoutDialogProps] = useState<DialogProps>({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const navigate = useNavigate();
	const isMenuOpen = Boolean(anchorEl);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
	const menuId = "primary-search-account-menu";
	const mobileMenuId = "primary-search-account-menu-mobile";

	const handleLogoutClick = useCallback(() => {
		localStorage.clear();
		setLogoutDialogProps(
			Object.assign({}, logoutDialogProps, {
				openDialog: true,
				title: "Thank you",
				content: "You have successfully logged out of CuraFoot CRM!",
				actions: [
					{
						label: "Okay",
						callback: () => {
							navigate("/login");
							setLogoutDialogProps(Object.assign({}, logoutDialogProps, { openDialog: false }));
						}
					}
				]
			})
		);
	}, [logoutDialogProps, navigate]);

	const handleProfileMenuOpen = (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMobileMenuClose = () => setMobileMoreAnchorEl(null);

	const handleMenuClose = () => {
		setAnchorEl(null);
		handleMobileMenuClose();
	};

	const handleMobileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMobileMoreAnchorEl(event.currentTarget);
	};

	const handleDrawerToggle = useCallback(() => {
		setMobileOpen(!mobileOpen);
		if (mobileOpen === true) {
			setDrawerWidth(0);
		} else {
			setDrawerWidth(240);
		}
	}, [mobileOpen]);

	const renderMenu = (
		<Popover
			id={menuId}
			open={isMenuOpen}
			anchorEl={anchorEl}
			onClose={handleMenuClose}
			anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			transformOrigin={{ vertical: "top", horizontal: "center" }}
		>
			<div className="profile-menu ">
				<List>
					<ListItem button onClick={handleLogoutClick}>
						<ListItemAvatar>
							<Avatar className="list-avatar">
								<ExitToAppIcon />
							</Avatar>
						</ListItemAvatar>
						<ListItemText primary="Logout" />
					</ListItem>
				</List>
			</div>
		</Popover>
	);

	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{ vertical: "top", horizontal: "right" }}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			<div className="sec-desk">
				<MenuItem onClick={handleProfileMenuOpen}>
					<IconButton
						aria-label="account of current user"
						aria-controls="primary-search-account-menu"
						aria-haspopup="true"
						color="inherit"
					>
						<AccountCircle style={{ color: "#4e4e4e" }} />
					</IconButton>
					<p>Profile</p>
				</MenuItem>
			</div>
		</Menu>
	);

	const drawer = (
		<div id="admin-dashboard" className="admin-dashboard">
			<div className={classes.toolbar} />
			<div className="welcome-container">
				<div className="sidebar-icon-container">
					{mobileOpen && (
						<IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
							<Avatar style={{ width: 24, height: 24, backgroundColor: "#25a18e" }}>
								<ChevronLeftIcon />
							</Avatar>
						</IconButton>
					)}
				</div>
				<div className="sidebar-logo-container">
					<a href="/lab/scans">
						<img src={curaFootLogoWhite} alt="" />
					</a>
				</div>

				<div className="dashboard-type-text" id="dashboard-type-text">
					<div>{initCapitalize(ROLES.lab)}</div>
				</div>

				<StyledBadge
					overlap="circular"
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					variant="dot"
				>
					<Avatar alt="Lab" className={classes.avatarColor}>
						<div className="avatar-text">LAB</div>
					</Avatar>
				</StyledBadge>
				<div className="welcome-text" id="welcome-text">
					{user && user.first_name && user.last_name ? user.first_name + " " + user.last_name : null}
				</div>
			</div>
			<Divider />
			<List>
				{SIDEBAR_OPTIONS["LAB"]?.map((tab: Tab, tabIndex: number) => {
					return (
						<Accordion
							className="list-item-accordion"
							key={tabIndex}
							expanded={false}
							onClick={() => navigate(tab.route)}
						>
							<AccordionSummary
								expandIcon={tab.subTabs.length > 0 ? <ExpandMoreIcon /> : <div />}
								aria-controls="panel1a-content"
								id="panel1a-header"
								className="list-item-accordion-summary"
								style={{
									backgroundColor: selectedTab.index === tabIndex ? "#85CE36" : "inherit"
								}}
							>
								<div style={{ marginRight: 10 }}>{tab.icon()}</div>
								<div>{tab.caption}</div>
							</AccordionSummary>
						</Accordion>
					);
				})}
			</List>
		</div>
	);

	const container = window !== undefined ? () => window.document.body : undefined;

	return (
		<div className={classes.application + " admin-sidebar "}>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={logoutDialogProps} />
			</Suspense>
			<CssBaseline />
			<AppBar
				position="fixed"
				className={classes.appBar}
				style={{
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: drawerWidth,
					backgroundColor: "#EEEEEE",
					color: "black"
				}}
			>
				<Toolbar className="toolbar">
					{!mobileOpen && (
						<IconButton
							color="primary"
							aria-label="open drawer"
							edge="start"
							onClick={handleDrawerToggle}
							className={classes.menuButton}
						>
							<MenuIcon />
						</IconButton>
					)}
					<div className="appbar-header" id="appbar-header">
						{dashboardHeader}
					</div>

					<div className={classes.grow} />
					<div className={classes.sectionDesktop + " sec-desk"}>
						<Tooltip title="Profile" enterDelay={500} leaveDelay={200}>
							<IconButton
								edge="end"
								aria-label="account of current user"
								aria-controls={menuId}
								aria-haspopup="true"
								onClick={handleProfileMenuOpen}
								color="inherit"
							>
								<AccountCircle style={{ color: "#4e4e4e" }} />
							</IconButton>
						</Tooltip>
					</div>
					<div className={classes.sectionMobile}>
						<IconButton
							aria-label="show more"
							aria-controls={mobileMenuId}
							aria-haspopup="true"
							onClick={handleMobileMenuOpen}
							color="inherit"
						>
							<MoreIcon />
						</IconButton>
					</div>
				</Toolbar>
			</AppBar>
			{renderMobileMenu}
			{renderMenu}
			<nav
				className={classes.drawer}
				aria-label="mailbox folders"
				style={{
					[theme.breakpoints.up("md")]: {
						width: drawerWidth,
						flexShrink: 0
					}
				}}
			>
				<Drawer
					container={container}
					variant="persistent"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true
					}}
					style={{
						width: drawerWidth,
						backgroundColor: "#003545"
					}}
				>
					{drawer}
				</Drawer>
			</nav>
			<main className={classes.content + " main"}>
				<div className={classes.toolbar} />
				<Suspense fallback={<div />}>
					<Outlet />
				</Suspense>
			</main>
		</div>
	);
};

export default LabSidebar;
